import React from 'react';
import ReactIs from 'react-is';
import {
  assign,
  find,
  findIndex,
  flatten,
  defaults,
  sum,
  clamp,
  flowRight as compose,
} from 'lodash';
import {assoc} from '@packages/utilities/fp/utils';
import {weakMemo} from '@packages/utilities/memo';

const clampedGet = (array) => (index) =>
  array[clamp(index, 0, array.length - 1)];
const getChildren = (group) => group.children;

export const bindNull = (fn) => () => fn(null);

export const getValue = (props) => {
  return props.value == null ? props.children : props.value;
};

export const getLabel = (option) => {
  if (option) {
    return option.label || option.children || '';
  }

  return '';
};

const getButtonLabel = (option) => {
  if (option) {
    return option.buttonLabel || getLabel(option) || '';
  }

  return '';
};

const getProps = (element) => element && element.props;
const getOptions = (group) =>
  group &&
  defaults(
    {children: React.Children.map(group.props.children, getProps)},
    group.props
  );

export const DropdownGroupSymbol = Symbol('DropdownGroup');

export const groups = weakMemo((children) => {
  if (ReactIs.isFragment(children)) {
    return groups(children.props.children);
  }
  if (children.length > 0) {
    return children[0].props?.isGroup ||
      children[0].type?.displayName === DropdownGroupSymbol
      ? children.map(getOptions).filter(Boolean)
      : [{children: children.map(getProps).filter(Boolean)}];
  }

  if (
    React.isValidElement(children) &&
    (children.props.isGroup ||
      children.type?.displayName === DropdownGroupSymbol)
  ) {
    return [getOptions(children)];
  }

  return [];
});

const allOptions = (children) => flatten(groups(children).map(getChildren));

const isEnabled = (option) => !option.disabled && !option.header;
const allEnabledOptions = weakMemo((children) =>
  allOptions(children).filter(isEnabled)
);

const findIndexByValue = (value, options) =>
  findIndex(options, (option) => getValue(option) === value);

const findOptionByValue = (value, options) =>
  options[findIndexByValue(value, options)];

const seekValue = (offset) => (value, children) => {
  const options = allEnabledOptions(children);
  const currentIndex = findIndexByValue(value, options);
  const nextIndex =
    currentIndex === -1 && offset < 0
      ? options.length + offset
      : currentIndex + offset;

  return getValue(clampedGet(options)(nextIndex));
};

const nextValue = seekValue(1);
const prevValue = seekValue(-1);

const findMatch = (character, children) => {
  const lowerCaseCharacter = character.toLowerCase();
  const match = find(allEnabledOptions(children), (option) => {
    // find best match by looking at label property then buttonLabel property
    let label = getLabel(option);
    if (typeof label !== 'string') {
      label = getButtonLabel(option);
    }
    if (typeof label !== 'string') {
      return false;
    }
    return label.toLowerCase()[0] === lowerCaseCharacter;
  });

  if (match) {
    return getValue(match);
  }

  return null;
};

const selectedOption = (value, children) =>
  findOptionByValue(value, allEnabledOptions(children));

export const getSelectedLabelForButton = compose(
  getButtonLabel,
  selectedOption
);

export const getOptionHeight = (option, group, theme) => {
  if (option.height) {
    return option.height;
  }

  if (option.header) {
    return theme.gutters.x4 * 5;
  }

  // group height is fixed, so need to squish all option heights a bit (CC)
  if (group.customHeight) {
    return group.customHeight / group.children.length;
  }

  return theme.control.height;
};

const getOptionHeightWithMargins = (option, group, theme) =>
  getOptionHeight(option, group, theme) +
  getOptionMargins(option, group, theme).total;

export const getOptionVerticalPadding = (
  {gutters, control, lineHeights},
  option
) =>
  (((option && (option.header ? gutters.x4 * 5 : option.height)) ||
    control.height) -
    lineHeights.copy) /
  2;

export const getOptionMargins = (option, group, theme) => {
  let top = 0;
  let bottom = 0;

  const index = group.children.indexOf(option);

  if (option.header) {
    // if the header is the first item in the group, then do not include marginTop since there's already group padding (CC)
    if (index > 0) {
      top += getOptionVerticalPadding(theme, option);
    }

    // if at the end (probably doesn't happen often), then don't include bottom margin because of included padding
    if (index < group.children.length - 1) {
      bottom += getOptionVerticalPadding(theme, option);
    }
  }

  return {top, bottom, total: top + bottom};
};

export const calculateOptionPositions = weakMemo((theme) =>
  weakMemo((group) => {
    let currentTop = theme.dropdown.groupVerticalPadding;
    return group.children.map(
      (option) =>
        (currentTop += getOptionHeightWithMargins(option, group, theme)) +
        theme.dropdown.dividerWidth
    );
  })
);

const calculateGroupHeight = weakMemo((theme) =>
  weakMemo((group) => {
    const tops = calculateOptionPositions(theme)(group);
    return tops[tops.length - 1] + theme.dropdown.groupVerticalPadding;
  })
);

export const groupHeight = weakMemo((theme) =>
  weakMemo((group) => group.customHeight || calculateGroupHeight(theme)(group))
);

export const menuHeight = weakMemo((theme) =>
  weakMemo((children) => sum(groups(children).map(groupHeight(theme))))
);

export const indexInGroup =
  ({control, card}) =>
  (top) =>
    Math.floor(
      (top - card.borderRadius - control.borderWidth) / control.height
    );

const optionOffset = weakMemo((theme) => (acc, option) => {
  acc[0][getValue(option)] = acc[1];
  acc[1] += theme.control.height;
  return acc;
});

const offsetMap = weakMemo((theme) => {
  const groupPadding = theme.card.borderRadius + theme.control.borderWidth;

  return weakMemo(
    (children) =>
      groups(children).reduce(
        (acc, group) => {
          acc[1] += groupPadding;
          acc = group.children.reduce(optionOffset(theme), acc);
          acc[1] += groupPadding;
          return acc;
        },
        [{}, 0]
      )[0]
  );
});

export const selectedTop = weakMemo(
  (theme) => (value, children) => offsetMap(theme)(children)[value] || 0
);

const initialScrollView = {
  scrollTop: 0,
  scrollLeft: 0,
};

const initialState = assign(
  {},
  {
    open: false,
    value: null,
    preselected: null,
    children: [],
    hasScrolled: false,
    scrollView: initialScrollView,
  }
);

export const getInitialState = (props) =>
  props ? {...initialState, ...props} : initialState;

export const change = (state, value) => {
  return defaults(
    {
      preselected: null,
      open: false,
      scrollView: initialScrollView,
      hasScrolled: false,
      value,
    },
    state
  );
};

export const preselect = (state, value) => {
  return assoc('preselected')(value)(state);
};

export const open = (state) => {
  return assoc('open')(true)(preselect(state, state.value));
};

export const close = (state) => {
  return defaults(
    {
      open: false,
      scrollView: initialScrollView,
      hasScrolled: false,
    },
    state
  );
};

export const scroll = (state, scrollData) => {
  return defaults(
    {
      scrollView: defaults({}, scrollData, state.scrollView),
      hasScrolled: true,
    },
    state
  );
};

export const handleKeyDown = (state, key) => {
  if (key === 'Escape') {
    return close(state);
  }

  if (key === 'ArrowDown') {
    if (state.open) {
      return preselect(state, nextValue(state.preselected, state.children));
    }

    return open(state);
  }

  if (key === 'ArrowUp') {
    if (state.open) {
      return preselect(state, prevValue(state.preselected, state.children));
    }

    return open(state);
  }

  if (key === 'Enter' || key === ' ') {
    if (!state.open) {
      return open(state);
    } else if (state.preselected == null) {
      return close(state);
    } else {
      return change(state, state.preselected);
    }
  }

  // Prevent tabbing out of the select when menu is open.
  if (key === 'Tab' && state.open) {
    // TODO: shift+tab should reverse order
    return preselect(state, nextValue(state.preselected, state.children));
  }

  if (key.length === 1) {
    const match = findMatch(key, state.children);

    if (match != null) {
      return preselect(state, match);
    }
  }

  return state;
};
